/**
 * PersistentStorageService
 * Handles persistent storage of user data across sessions
 * Stores data in localStorage with encryption for sensitive information
 */

class PersistentStorageService {
  constructor() {
    this.storageKey = 'jtncbot_persistent_data';
    this.encryptionKey = 'jtncbot_encryption_key_2024';
  }

  /**
   * Simple encryption for sensitive data
   */
  encrypt(data) {
    try {
      const jsonString = JSON.stringify(data);
      return btoa(jsonString);
    } catch (error) {
      console.warn('Encryption failed, storing as plain text:', error);
      return JSON.stringify(data);
    }
  }

  /**
   * Simple decryption for sensitive data
   */
  decrypt(encryptedData) {
    try {
      const jsonString = atob(encryptedData);
      return JSON.parse(jsonString);
    } catch (error) {
      console.warn('Decryption failed, trying plain text:', error);
      try {
        return JSON.parse(encryptedData);
      } catch (parseError) {
        console.error('Failed to parse data:', parseError);
        return null;
      }
    }
  }

  /**
   * Store data persistently
   */
  setItem(key, value, encrypt = false) {
    try {
      const data = this.getAllData();
      const dataToStore = encrypt ? this.encrypt(value) : value;
      
      data[key] = {
        value: dataToStore,
        encrypted: encrypt,
        timestamp: new Date().toISOString(),
        version: '1.0'
      };

      localStorage.setItem(this.storageKey, JSON.stringify(data));
      // Only log important operations, not every storage operation
      if (key !== 'lastVisitedPage' && key !== 'dashboardState') {
        console.log(`💾 Stored persistent data: ${key}`);
      }
      return true;
    } catch (error) {
      console.error(`Failed to store persistent data: ${key}`, error);
      return false;
    }
  }

  /**
   * Get data from persistent storage
   */
  getItem(key, defaultValue = null) {
    try {
      const data = this.getAllData();
      const item = data[key];
      
      if (!item) {
        return defaultValue;
      }

      if (item.encrypted) {
        return this.decrypt(item.value);
      }

      return item.value;
    } catch (error) {
      console.error(`Failed to get persistent data: ${key}`, error);
      return defaultValue;
    }
  }

  /**
   * Remove specific data
   */
  removeItem(key) {
    try {
      const data = this.getAllData();
      delete data[key];
      localStorage.setItem(this.storageKey, JSON.stringify(data));
      console.log(`🗑️ Removed persistent data: ${key}`);
      return true;
    } catch (error) {
      console.error(`Failed to remove persistent data: ${key}`, error);
      return false;
    }
  }

  /**
   * Get all stored data
   */
  getAllData() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Failed to get all persistent data:', error);
      return {};
    }
  }

  /**
   * Clear all persistent data
   */
  clearAll() {
    try {
      localStorage.removeItem(this.storageKey);
      console.log('🗑️ Cleared all persistent data');
      return true;
    } catch (error) {
      console.error('Failed to clear persistent data:', error);
      return false;
    }
  }

  /**
   * Store user preferences
   */
  storeUserPreferences(preferences) {
    return this.setItem('userPreferences', preferences, false);
  }

  /**
   * Get user preferences
   */
  getUserPreferences() {
    return this.getItem('userPreferences', {
      theme: 'dark',
      language: 'en',
      notifications: true,
      autoRefresh: true,
      lastVisitedPage: '/dashboard'
    });
  }

  /**
   * Store broker connection status
   */
  storeBrokerConnections(connections) {
    return this.setItem('brokerConnections', connections, false);
  }

  /**
   * Get broker connection status
   */
  getBrokerConnections() {
    return this.getItem('brokerConnections', {
      tradier: false,
      tastytrade: false,
      schwab: false,
      interactive_brokers: false
    });
  }

  /**
   * Store last visited page
   */
  storeLastVisitedPage(page) {
    return this.setItem('lastVisitedPage', page, false);
  }

  /**
   * Get last visited page
   */
  getLastVisitedPage() {
    return this.getItem('lastVisitedPage', '/dashboard');
  }

  /**
   * Store dashboard state
   */
  storeDashboardState(state) {
    return this.setItem('dashboardState', state, false);
  }

  /**
   * Get dashboard state
   */
  getDashboardState() {
    return this.getItem('dashboardState', {
      stocks: [],
      portfolioValue: 0,
      selectedTab: 'overview',
      filters: {},
      sortBy: 'symbol',
      sortOrder: 'asc'
    });
  }

  /**
   * Store form data for recovery
   */
  storeFormData(formName, data) {
    return this.setItem(`form_${formName}`, data, false);
  }

  /**
   * Get form data for recovery
   */
  getFormData(formName) {
    return this.getItem(`form_${formName}`, null);
  }

  /**
   * Check if user has active broker connections
   */
  hasActiveBrokerConnections() {
    const connections = this.getBrokerConnections();
    return Object.values(connections).some(connected => connected);
  }

  /**
   * Store user session info
   */
  storeSessionInfo(sessionInfo) {
    return this.setItem('sessionInfo', sessionInfo, false);
  }

  /**
   * Get user session info
   */
  getSessionInfo() {
    return this.getItem('sessionInfo', {
      lastLogin: null,
      loginCount: 0,
      preferredBroker: null,
      hasCompletedOnboarding: false
    });
  }

  /**
   * Store Strategy Center data (stocks with values, types, status, etc.)
   */
  storeStrategyCenterData(stocks) {
    return this.setItem('strategyCenterData', stocks, false);
  }

  /**
   * Get Strategy Center data
   */
  getStrategyCenterData() {
    return this.getItem('strategyCenterData', [
      { symbol: 'SPX', value: 0, type: null, hedge: false, status: '', quanlity: 0 },
      { symbol: 'SPY', value: 0, type: null, hedge: false, status: '', quanlity: 0 },
      { symbol: 'QQQ', value: 0, type: null, hedge: false, status: '', quanlity: 0 }
    ]);
  }
}

// Create singleton instance
const persistentStorage = new PersistentStorageService();

export default persistentStorage; 